import { type ChangeEvent } from 'react';

import { Col } from '@opendoor/atoms/box';
import { Input } from '@opendoor/atoms/input';
import { PhoneInput } from '@opendoor/atoms/phone-input';
import { Radio } from '@opendoor/atoms/radio';
import { Text } from '@opendoor/atoms/text';
import { isValidEmailAddress } from 'components/shared/emailValidation';
import {
  isAgentIdentifierValid,
  isAgentNamePairValid,
  isAgentNameValid,
  isAgentPhoneValid,
  isFullNameValid,
  type OfferPortalWizardContext,
} from 'state_machines/OfferPortalWizardMachine';

import { type ListingDetails, showsAgentLicenseNumber, showsMlsOfficeId } from './listingDetails';
import { MAX_AGENT_FIELD_LENGTH } from './offerSubmission';
import {
  AGENT_TYPE_OPTIONS,
  CardHeading,
  FIELD_CARD,
  FIELD_INPUT,
  FieldError,
  fieldLabel,
  RADIO_GROUP_AS_CARD_TITLE,
  RADIO_ROW,
  type Send,
} from './wizardFields';

const TOO_LONG_MESSAGE = `Enter at most ${MAX_AGENT_FIELD_LENGTH} characters.`;
const NAME_PAIR_TOO_LONG_MESSAGE = `First and last name together can be at most ${MAX_AGENT_FIELD_LENGTH} characters.`;

const identifierErrorMessage = (value: string, emptyMessage: string): string =>
  value.trim() === '' ? emptyMessage : TOO_LONG_MESSAGE;

const nameErrorMessage = (name: string, emptyMessage: string): string =>
  !isFullNameValid(name) ? emptyMessage : TOO_LONG_MESSAGE;

/** Step 4 of 6 of the offer wizard — the submitting agent's own details. */
const SubmittingAsStep = ({
  listing,
  context,
  send,
}: {
  listing: ListingDetails;
  context: OfferPortalWizardContext;
  send: Send;
}) => {
  const phoneOk = isAgentPhoneValid(context.agentPhone);
  const phoneViolation = context.fieldViolations['contactPhone'];
  const phoneInvalid = (context.showError && !phoneOk) || phoneViolation != null;
  const agentField = ({
    id,
    label,
    value,
    onValue,
    invalid,
    errorMessage,
    type = 'text',
    violation,
  }: {
    id: string;
    label: string;
    value: string;
    onValue: (value: string) => void;
    invalid: boolean;
    errorMessage?: string;
    type?: 'text' | 'email';
    violation?: string;
  }) => {
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      onValue(event.target.value);
    };
    return (
      <Col className="gap-2" key={id}>
        <Text as="label" htmlFor={id} className={fieldLabel(invalid || violation != null)}>
          {label}
        </Text>
        <Input
          id={id}
          type={type}
          value={value}
          onChange={handleChange}
          size="xs"
          className={FIELD_INPUT}
          aria-label={label}
          aria-invalid={invalid || violation != null}
          error={invalid || violation != null}
          analyticsName={`cosmos-make-offer-agent-${id}`}
        />
        {violation != null ? <FieldError>{violation}</FieldError> : null}
        {invalid && errorMessage ? <FieldError>{errorMessage}</FieldError> : null}
      </Col>
    );
  };
  const handleFirstNameChange = (value: string) => {
    send({ type: 'SET_AGENT_FIRST_NAME', value });
  };
  const handleLastNameChange = (value: string) => {
    send({ type: 'SET_AGENT_LAST_NAME', value });
  };
  const handleAgentTypeChange = (value: string) => {
    send({ type: 'SET_AGENT_TYPE', value: value === 'non_mls' ? 'non_mls' : 'mls' });
  };
  const handleMlsIdChange = (value: string) => {
    send({ type: 'SET_AGENT_MLS_ID', value });
  };
  const handleMlsOfficeIdChange = (value: string) => {
    send({ type: 'SET_AGENT_MLS_OFFICE_ID', value });
  };
  const handleEmailChange = (value: string) => {
    send({ type: 'SET_AGENT_EMAIL', value });
  };
  const handlePhoneChange = (value: string) => {
    send({ type: 'SET_AGENT_PHONE', value });
  };
  const handleLicenseNumberChange = (value: string) => {
    send({ type: 'SET_AGENT_LICENSE_NUMBER', value });
  };
  return (
    <>
      <Col className={FIELD_CARD}>
        {agentField({
          id: 'your-first-name',
          label: 'Your first name',
          value: context.agentFirstName,
          onValue: handleFirstNameChange,
          invalid: context.showError && !isAgentNameValid(context.agentFirstName),
          errorMessage: nameErrorMessage(context.agentFirstName, 'Enter your first name.'),
          violation:
            context.fieldViolations['agentFirstName'] ?? context.fieldViolations['contactName'],
        })}
        {agentField({
          id: 'your-last-name',
          label: 'Your last name',
          value: context.agentLastName,
          onValue: handleLastNameChange,
          invalid:
            context.showError &&
            (!isAgentNameValid(context.agentLastName) ||
              !isAgentNamePairValid(context.agentFirstName, context.agentLastName)),
          errorMessage: isAgentNameValid(context.agentLastName)
            ? NAME_PAIR_TOO_LONG_MESSAGE
            : nameErrorMessage(context.agentLastName, 'Enter your last name.'),
          violation: context.fieldViolations['agentLastName'],
        })}
        {agentField({
          id: 'your-email',
          label: 'Your email',
          value: context.agentEmail,
          onValue: handleEmailChange,
          invalid: context.showError && !isValidEmailAddress(context.agentEmail.trim()),
          errorMessage: 'Check if this email address is correct.',
          type: 'email',
          violation: context.fieldViolations['contactEmail'],
        })}
        <Col className="gap-2">
          <Text as="label" htmlFor="your-phone" className={fieldLabel(phoneInvalid)}>
            Phone number
          </Text>
          <PhoneInput
            id="your-phone"
            value={context.agentPhone}
            onValueChange={handlePhoneChange}
            size="xs"
            className={FIELD_INPUT}
            aria-label="Phone number"
            aria-invalid={phoneInvalid}
            error={phoneInvalid}
            analyticsName="cosmos-make-offer-agent-your-phone"
          />
          {phoneViolation != null ? <FieldError>{phoneViolation}</FieldError> : null}
          {context.showError && !phoneOk ? (
            <FieldError>Enter a valid phone number.</FieldError>
          ) : null}
        </Col>
      </Col>
      <Col className={FIELD_CARD}>
        <Radio.Group
          className={RADIO_GROUP_AS_CARD_TITLE}
          label={<CardHeading title="Agent type?" />}
          value={context.agentType}
          onValueChange={handleAgentTypeChange}
        >
          {AGENT_TYPE_OPTIONS.map((option) => (
            <Radio.Item
              key={option.value}
              label={option.label}
              value={option.value}
              className={RADIO_ROW}
            />
          ))}
        </Radio.Group>
        {context.fieldViolations['agentType'] != null ? (
          <FieldError>{context.fieldViolations['agentType']}</FieldError>
        ) : null}
        {context.agentType === 'mls'
          ? agentField({
              id: 'agent-mls-id',
              label: 'Agent MLS ID',
              value: context.agentMlsId,
              onValue: handleMlsIdChange,
              invalid: context.showError && !isAgentIdentifierValid(context.agentMlsId),
              errorMessage: identifierErrorMessage(context.agentMlsId, 'Enter your agent MLS ID.'),
              violation: context.fieldViolations['agentMlsId'],
            })
          : null}
        {showsMlsOfficeId(listing, context.agentType)
          ? agentField({
              id: 'mls-office-id',
              label: 'MLS office ID',
              value: context.agentMlsOfficeId,
              onValue: handleMlsOfficeIdChange,
              invalid: context.showError && !isAgentIdentifierValid(context.agentMlsOfficeId),
              errorMessage: identifierErrorMessage(
                context.agentMlsOfficeId,
                'Enter your MLS office ID.',
              ),
              violation: context.fieldViolations['agentMlsOfficeId'],
            })
          : null}
        {showsAgentLicenseNumber(listing, context.agentType)
          ? agentField({
              id: 'agent-license-number',
              label: 'Agent license number',
              value: context.agentLicenseNumber,
              onValue: handleLicenseNumberChange,
              invalid: context.showError && !isAgentIdentifierValid(context.agentLicenseNumber),
              errorMessage: identifierErrorMessage(
                context.agentLicenseNumber,
                'Enter your agent license number.',
              ),
              violation: context.fieldViolations['agentLicenseNumber'],
            })
          : null}
      </Col>
    </>
  );
};

export default SubmittingAsStep;
